import { type IServerSideGetRowsRequest } from 'ag-grid-community';
import invert from 'lodash/invert';

import { type ViewColumn } from 'common/types/viewColumn';
import { composeQuery, select, userStar, c } from 'common/soql_builder';
import { type NamedSelect } from 'common/soql_builder/select';

import { type ColumnAggregation } from '../dataProviders/types';
import { getSoqlMetaBundle } from '../dataProviders/getSoqlMetaBundle';
import { type Hierarchy, type HierarchyColumnConfig, type Vif } from '../vif';

import { constructAgRequestFromHierarchy } from './ConstructAgRequest';
import { grandTotalQuery } from './TableDataGrandTotalHelpers';
import {
  createMeasureAliasMap,
  createSelectAliasMap,
  getSelectAggregationsQuery,
  unaliasRowData,
  generateWhereQuery,
} from './TableDataQueryHelpers';
import { findCorrespondingColumn } from './TableDataColumnHelpers';
import { isPivotInHierarchy } from './PivotHelpers';
import { generateCascadingPipedQuery, getAdvancedTotalingAggregations } from './TableDataFormulaHelpers';
import { type SoqlDataProvider } from '../dataProviders';
import { PIVOT_SEPARATOR } from '../views/agGridReact/Constants';
import { isTablePrintingImprovementFormattingEnabled } from './VifSelectors';
import { TableExportType } from '../views/agGridReact/types';

/**
 * Generates a lookup key for O(1) subtotal access.
 * Format: "level:groupKey1|groupKey2|...:column"
 */
export function generateLookupKey(level: number, groupKeys: string[], column: string): string {
  const groupKeyString = groupKeys.join('|');
  return `${level}:${groupKeyString}:${column}`;
}

/**
 * Builds lookup maps for a single level of subtotal data
 */
export function buildLookupMapsForLevel(
  level: number,
  unaliasedResults: unknown[],
  rowGroupCols: { id: string }[],
  valueCols: { id: string }[],
  subtotalLookup: Map<string, unknown>,
  levelMetadata: Map<number, { groupColumns: string[]; valueColumns: string[] }>,
  pivotCols?: { id: string }[],
): void {
  const numGroupColumns = level + 1;
  const groupColumns = rowGroupCols.slice(0, numGroupColumns).map((col) => col.id);
  const valueColumns = valueCols.map((col) => col.id);
  const pivotColumns = pivotCols?.map((col) => col.id) || [];

  levelMetadata.set(level, { groupColumns, valueColumns });

  // Build lookup entries for each row
  unaliasedResults.forEach((row) => {
    if (typeof row === 'object' && row !== null) {
      const rowData = row as Record<string, unknown>;
      const groupKeys = groupColumns.map((col) => {
        const value = rowData[col];
        return typeof value === 'string' ? value : String(value ?? 'null');
      });

      // Store each value column for this group combination
      // Exclude both group columns AND pivot columns from being stored as subtotal values
      Object.entries(rowData).forEach(([column, value]) => {
        if (!groupColumns.includes(column) && !pivotColumns.includes(column) && value !== undefined) {
          const key = generateLookupKey(level, groupKeys, column);
          subtotalLookup.set(key, value);
        }
      });
    }
  });
}

/**
 * Creates alias mappings for a specific level
 */
export function createAliasMapping(
  level: number,
  rowGroupCols: { id: string }[],
  agRequest: IServerSideGetRowsRequest,
): Record<string, string> {
  const numGroupColumns = level + 1;
  const aliasMap: Record<string, string> = {};

  for (let i = 0; i < numGroupColumns; i++) {
    const groupCol = rowGroupCols[i];
    const alias = i === 0 ? '__select_alias__' : `__select_alias_${i}__`;
    aliasMap[alias] = groupCol.id;
  }

  const measureAliasMap = createMeasureAliasMap(agRequest);
  return { ...aliasMap, ...measureAliasMap };
}

export interface ExportTotalsResult {
  grandTotals: Record<string, any> | Array<Record<string, any>> | null;
  subtotalLookup: Map<string, unknown>;
  levelMetadata: Map<number, { groupColumns: string[]; valueColumns: string[] }>;
}

/**
 * Helper function to build pivot mode subtotals query
 */
function buildPivotSubtotalQuery(
  columns: ViewColumn[],
  agRequest: IServerSideGetRowsRequest,
  numGroupColumns: number,
  hierarchyConfig?: Hierarchy,
  nonStandardAggregations?: ColumnAggregation[] | null,
  legacyWhereClause?: string,
  isAllDataExport: boolean = false,
): string {
  const { rowGroupCols, pivotCols, valueCols } = agRequest;
  const selectQueries: NamedSelect[] = [];
  const groupQueries: ViewColumn[] = [];

  // First include all row grouping columns up to the specified level
  for (let i = 0; i < numGroupColumns; i++) {
    const groupCol = rowGroupCols[i];
    const colId = findCorrespondingColumn(columns, groupCol);
    if (colId) {
      const alias = i === 0 ? '__select_alias__' : `__select_alias_${i}__`;
      selectQueries.push(select(colId, alias));
      groupQueries.push(colId);
    }
  }

  // In pivot mode, also include pivot columns in the group by
  pivotCols.forEach((pivotCol, idx) => {
    const pivotColId = findCorrespondingColumn(columns, pivotCol);
    if (pivotColId) {
      const pivotAlias = `__pivot_alias_${idx}__`;
      selectQueries.push(select(pivotColId, pivotAlias));
      groupQueries.push(pivotColId);
    }
  });

  let aggregationSelects: NamedSelect[] = [];

  if (hierarchyConfig) {
    const startLevelIndex = numGroupColumns;
    aggregationSelects = getAdvancedTotalingAggregations(
      valueCols,
      columns,
      hierarchyConfig.columnConfigurations,
      startLevelIndex,
      nonStandardAggregations,
    );
  }

  if (aggregationSelects.length === 0) {
    aggregationSelects = getSelectAggregationsQuery(valueCols, columns, nonStandardAggregations);
  }

  selectQueries.push(...aggregationSelects);

  // when the user picks "all data" in the xlsx-formatted export modal, run the aggregation
  // directly against the filtered dataset so pivot subtotals reflect every row instead of the
  // 100k inner-pipe slice. matches buildNonPivotSubtotalQuery's flat composeQuery shape.
  if (isAllDataExport) {
    return composeQuery({
      selects: selectQueries,
      groups: groupQueries,
      legacyWhereClause,
    });
  }

  const innerQueryParams = {
    starSelects: [userStar()], // SELECT *
    legacyWhereClause,
    limit: 100000,
  };

  const outerQueryParams = {
    selects: selectQueries,
    groups: groupQueries,
  };

  const pipedQuery = composeQuery(c.pipe(innerQueryParams, outerQueryParams));
  return pipedQuery;
}

/**
 * Helper function to build non-pivot mode subtotals query
 */
function buildNonPivotSubtotalQuery(
  columns: ViewColumn[],
  agRequest: IServerSideGetRowsRequest,
  numGroupColumns: number,
  legacyWhereClause: string,
  hierarchyColumnConfigs?: HierarchyColumnConfig[],
  nonStandardAggregations?: ColumnAggregation[] | null,
): string {
  const { rowGroupCols, valueCols } = agRequest;

  const currentQueryGroupLevel = numGroupColumns - 1;

  const cascadingGroupKeys = Array(Math.max(0, currentQueryGroupLevel)).fill('');
  const subtotalAgRequest = { ...agRequest, groupKeys: cascadingGroupKeys };

  const whereParts = generateWhereQuery(columns, [], rowGroupCols);

  const pipedQuery = generateCascadingPipedQuery({
    columns,
    legacyWhereClause,
    agRequest: subtotalAgRequest,
    currentQueryGroupLevel,
    hierarchyConfigs: hierarchyColumnConfigs,
    nonStandardAggregations,
    whereParts,
    isExportQuery: true,
  });

  if (pipedQuery) {
    return pipedQuery;
  }

  const groupColumnMappings: Array<{ colId: ViewColumn; alias: string }> = [];

  for (let i = 0; i < numGroupColumns; i++) {
    const groupCol = rowGroupCols[i];
    const colId = findCorrespondingColumn(columns, groupCol);
    if (colId) {
      const alias = i === 0 ? '__select_alias__' : `__select_alias_${i}__`;
      groupColumnMappings.push({ colId, alias });
    }
  }

  const selectQuery = () => {
    const selectQueries: NamedSelect[] = [];

    // Add select queries for all group columns
    groupColumnMappings.forEach(({ colId, alias }) => {
      selectQueries.push(select(colId, alias));
    });

    // Add aggregation selects
    let aggregationSelects: NamedSelect[] = [];

    if (hierarchyColumnConfigs) {
      const startLevelIndex = numGroupColumns;
      aggregationSelects = getAdvancedTotalingAggregations(
        valueCols,
        columns,
        hierarchyColumnConfigs,
        startLevelIndex,
        nonStandardAggregations,
      );
    }

    if (aggregationSelects.length === 0) {
      aggregationSelects = getSelectAggregationsQuery(valueCols, columns, nonStandardAggregations);
    }

    selectQueries.push(...aggregationSelects);
    return selectQueries;
  };

  const groupQuery = () => {
    return groupColumnMappings.map(({ colId }) => colId);
  };

  // Uncomment if we want to support partial results subtotal
  // const innerQueryParams = {
  //   starSelects: [userStar()],
  //   legacyWhereClause,
  //   limit: 100000,
  // };

  // const outerQueryParams = {
  //   selects: selectQuery(),
  //   groups: groupQuery(),
  // };
  // return composeQuery(c.pipe(innerQueryParams, outerQueryParams));

  const query = composeQuery({
    selects: selectQuery(),
    groups: groupQuery(),
    legacyWhereClause,
  });

  return query;
}

/**
 * Query builder for fetching subtotals at a specific hierarchy level
 * Emulates the structure of tableDataQuery but focuses on grouping by all columns
 * from the first up to and including the target level
 * Automatically detects pivot mode and uses appropriate query building logic
 */
export function subTotalsQuery(
  columns: ViewColumn[],
  agRequest: IServerSideGetRowsRequest,
  numGroupColumns: number,
  legacyWhereClause: string,
  hierarchyConfig?: Hierarchy,
  nonStandardAggregations?: ColumnAggregation[] | null,
  applyAdvancedFormulas: boolean = true,
  isAllDataExport: boolean = false,
): string {
  const isPivot = hierarchyConfig ? isPivotInHierarchy(hierarchyConfig) : false;

  // when advanced formulas are gated off, drop columnConfigurations from the per-builder input so
  // the cascading/aggregation builders fall back to standard aggregations instead of pulling in
  // advanced totaling semantics. pivot detection above still uses the full hierarchyConfig.
  const formulaHierarchyConfig = applyAdvancedFormulas ? hierarchyConfig : undefined;

  if (isPivot) {
    return buildPivotSubtotalQuery(
      columns,
      agRequest,
      numGroupColumns,
      formulaHierarchyConfig,
      nonStandardAggregations,
      legacyWhereClause,
      isAllDataExport,
    );
  } else {
    return buildNonPivotSubtotalQuery(
      columns,
      agRequest,
      numGroupColumns,
      legacyWhereClause,
      formulaHierarchyConfig?.columnConfigurations,
      nonStandardAggregations,
    );
  }
}

/**
 * Fetches grand totals with Advanced Totaling custom formulas applied server-side
 *
 * @param vif - The VIF configuration
 * @param displayableColumns - Columns to display in the export
 * @param agRequest - AG Grid request configuration
 * @param hierarchyConfigs - Optional hierarchy column configurations for custom formulas
 * @param legacyWhereClause - Optional WHERE clause for filtering
 * @param nonStandardAggregations - Optional custom aggregation definitions
 * @returns Grand total data or null if no data found
 */
export async function fetchGrandTotalsForExport(
  vif: Vif,
  displayableColumns: ViewColumn[],
  agRequest: IServerSideGetRowsRequest,
  hierarchyColumnConfig?: HierarchyColumnConfig[],
  legacyWhereClause?: string,
  nonStandardAggregations?: ColumnAggregation[] | null,
  isPivot?: boolean,
): Promise<Record<string, any> | null> {
  try {
    const { soqlDataProvider } = getSoqlMetaBundle(vif);

    const grandTotalRequest = {
      ...agRequest,
      groupKeys: [],
    };

    const aliasedGrandTotalRow = await soqlDataProvider.invokeSoqlQuery(grandTotalQuery, [
      displayableColumns,
      legacyWhereClause || '',
      grandTotalRequest,
      nonStandardAggregations,
      hierarchyColumnConfig,
    ]);

    if (!aliasedGrandTotalRow || aliasedGrandTotalRow.length === 0) {
      return null;
    }

    const aggAliases = createMeasureAliasMap(grandTotalRequest);
    let selectAliases: { [key: string]: string } = {};

    if (isPivot) {
      const selectAliasMap = createSelectAliasMap(grandTotalRequest, displayableColumns);
      selectAliases = invert(selectAliasMap);
    }

    const unaliasedData = unaliasRowData(aliasedGrandTotalRow, {
      ...aggAliases,
      ...selectAliases,
    });

    // TODO EN-92427 [export-pivot-return-type]: Ideally, we should always return unaliasedData as an array, whether the data is
    // pivoted or not, to keep things consistent and avoid confusion. Right now, most
    // grandTotals consumers expect a Record<string, any>, which leads to mixed return types
    // and can cause issues, especially in PDF and XLSX exports. A separate ticket will be
    // created to clean this up and make the return type consistent.
    return isPivot ? unaliasedData : unaliasedData[0];
  } catch (error) {
    console.error('Error fetching grand totals for export:', error);
    return null;
  }
}

/**
 * Fetches subtotals for each grouping level with Advanced Totaling custom formulas applied server-side
 */
export async function fetchSubTotalsForExport(
  vif: Vif,
  displayableColumns: ViewColumn[],
  agRequest: IServerSideGetRowsRequest,
  exportType: TableExportType,
  hierarchyConfig?: Hierarchy,
  legacyWhereClause?: string,
  nonStandardAggregations?: ColumnAggregation[] | null,
  applyAdvancedFormulas: boolean = true,
  isAllDataExport: boolean = false,
): Promise<{
  subtotalLookup: Map<string, unknown>;
  levelMetadata: Map<number, { groupColumns: string[]; valueColumns: string[] }>;
}> {
  const { soqlDataProvider } = getSoqlMetaBundle(vif);
  const { rowGroupCols, pivotCols, valueCols } = agRequest;
  const subtotalLookup = new Map<string, unknown>();
  const levelMetadata = new Map<number, { groupColumns: string[]; valueColumns: string[] }>();

  if (!rowGroupCols || rowGroupCols.length === 0) {
    return { subtotalLookup, levelMetadata };
  }

  // Create promises for fetching subtotals at each hierarchy level (0 to rowGroupCols.length-1)
  // Each promise queries for aggregated data grouped by columns from level 0 up to the current level

  let levelPromises;
  const isPivot = isPivotInHierarchy(hierarchyConfig as Hierarchy);

  // Handle pivot mode if active
  if (isPivot && pivotCols.length > 0 && valueCols.length > 0) {
    levelPromises = createPivotLevelPromises(
      rowGroupCols,
      soqlDataProvider,
      displayableColumns,
      agRequest,
      subtotalLookup,
      levelMetadata,
      exportType,
      legacyWhereClause,
      hierarchyConfig,
      nonStandardAggregations,
      pivotCols,
      valueCols,
      applyAdvancedFormulas,
      isAllDataExport,
    );
  } else {
    levelPromises = Array.from({ length: rowGroupCols.length }, async (_, level) => {
      const numGroupColumns = level + 1;

      const aliasedResults = await soqlDataProvider.invokeSoqlQuery(subTotalsQuery, [
        displayableColumns,
        agRequest,
        numGroupColumns,
        legacyWhereClause,
        hierarchyConfig,
        nonStandardAggregations,
        applyAdvancedFormulas,
        isAllDataExport,
      ]);

      const fullAliasMap = createAliasMapping(level, rowGroupCols, agRequest);
      const unaliasedResults = unaliasRowData(aliasedResults, fullAliasMap);

      // Build lookup maps directly here to avoid double-looping
      buildLookupMapsForLevel(
        level,
        unaliasedResults,
        rowGroupCols,
        valueCols,
        subtotalLookup,
        levelMetadata,
      );

      return { level, unaliasedResults };
    });
  }

  try {
    await Promise.all(levelPromises);
  } catch (error) {
    console.error('Error fetching subtotals for export:', error);
    return { subtotalLookup: new Map(), levelMetadata: new Map() };
  }

  return { subtotalLookup, levelMetadata };
}

/**
 * Creates level promises for pivot mode subtotal fetching
 */
export function createPivotLevelPromises(
  rowGroupCols: { id: string }[],
  soqlDataProvider: SoqlDataProvider,
  displayableColumns: ViewColumn[],
  agRequest: IServerSideGetRowsRequest,
  subtotalLookup: Map<string, unknown>,
  levelMetadata: Map<number, { groupColumns: string[]; valueColumns: string[] }>,
  exportType: TableExportType,
  legacyWhereClause?: string,
  hierarchyConfig?: Hierarchy,
  nonStandardAggregations?: ColumnAggregation[] | null,
  pivotCols: { id: string }[] = [],
  valueCols: { id: string }[] = [],
  applyAdvancedFormulas: boolean = true,
  isAllDataExport: boolean = false,
) {
  return Array.from({ length: rowGroupCols.length }, async (_, level) => {
    const numGroupColumns = level + 1;

    return soqlDataProvider
      .invokeSoqlQuery(subTotalsQuery, [
        displayableColumns,
        agRequest,
        numGroupColumns,
        legacyWhereClause,
        hierarchyConfig,
        nonStandardAggregations,
        applyAdvancedFormulas,
        isAllDataExport,
      ])
      .then((aliasedResults: unknown[]) => {
        // Create alias map for row group columns
        const aliasMap: Record<string, string> = {};
        for (let i = 0; i < numGroupColumns; i++) {
          const groupCol = rowGroupCols[i];
          const alias = i === 0 ? '__select_alias__' : `__select_alias_${i}__`;
          aliasMap[alias] = groupCol.id;
        }

        const measureAliasMap = createMeasureAliasMap(agRequest);
        let fullAliasMap = { ...aliasMap, ...measureAliasMap };

        // For pivot mode, we need to transform the results
        // Create a map for pivot columns if present
        const pivotAliasMap: Record<string, string> = {};

        // Add pivot column aliases if in pivot mode
        pivotCols.forEach((pivCol, idx) => {
          const pivAlias = `__pivot_alias_${idx}__`;
          pivotAliasMap[pivAlias] = pivCol.id;
        });

        fullAliasMap = { ...aliasMap, ...pivotAliasMap, ...measureAliasMap };
        // First unalias using the full map
        const unaliasedResults = unaliasRowData(aliasedResults, fullAliasMap);

        // Now pivot the results similar to doClientSidePivot
        // but adapted for subtotals
        if (level === rowGroupCols.length - 1) {
          if (isTablePrintingImprovementFormattingEnabled() && exportType == 'PDF') {
            // For the deepest level, pivot the data to generate the correct column names
            // before building the lookup maps
            const pivotedResults = processLookupForLevel(
              unaliasedResults,
              pivotCols,
              valueCols,
              rowGroupCols,
              numGroupColumns,
              level,
              subtotalLookup,
              levelMetadata,
              true, // leaf level
            );
            return { level, unaliasedResults: pivotedResults };
          } else {
            // For the deepest level, we want to keep the raw results
            // as they include the aggregations we need for the custom formula
            return { level, unaliasedResults };
          }
        }

        const pivotedResults = processLookupForLevel(
          unaliasedResults,
          pivotCols,
          valueCols,
          rowGroupCols,
          numGroupColumns,
          level,
          subtotalLookup,
          levelMetadata,
        );

        return { level, unaliasedResults: pivotedResults };
      });
  });
}

/**
 * Processes pivot results for a specific grouping level and builds lookup maps.
 *
 * @param unaliasedResults - Raw subtotal data to be pivoted
 * @param pivotCols - Columns used for pivoting
 * @param valueCols - Value columns to aggregate
 * @param rowGroupCols - Row grouping columns
 * @param numGroupColumns - Number of group columns for this level
 * @param level - Current grouping level
 * @param subtotalLookup - Map to store lookup keys for O(1) access
 * @param levelMetadata - Metadata about each grouping level
 * @param leafLevel - Whether this is the deepest grouping level
 * @returns Pivoted subtotal results
 */
const processLookupForLevel = (
  unaliasedResults: SubtotalRow[],
  pivotCols: { id: string }[] = [],
  valueCols: { id: string }[] = [],
  rowGroupCols: { id: string }[],
  numGroupColumns: number,
  level: number,
  subtotalLookup: Map<string, unknown>,
  levelMetadata: Map<number, { groupColumns: string[]; valueColumns: string[] }>,
  leafLevel = false,
): SubtotalRow[] => {
  const extractColumnIds = (cols: { id: string }[]): string[] => cols.map((col) => col.id);

  const pivotedResults = pivotSubtotalResults(
    unaliasedResults,
    extractColumnIds(pivotCols),
    extractColumnIds(valueCols),
    extractColumnIds(rowGroupCols.slice(0, numGroupColumns)),
  );

  buildLookupMapsForLevel(
    level,
    pivotedResults,
    rowGroupCols,
    valueCols,
    subtotalLookup,
    levelMetadata,
    leafLevel ? pivotCols : undefined,
  );

  return pivotedResults;
};

type SubtotalRow = Record<string, unknown>;

/**
 * Helper function to pivot subtotal results
 * Similar to doClientSidePivot but specialized for subtotals
 */
function pivotSubtotalResults(
  rows: SubtotalRow[],
  pivotColumnIds: string[],
  valueColumnIds: string[],
  groupColumnIds: string[],
): SubtotalRow[] {
  if (!rows.length) {
    return [];
  }

  const pivotedRows: SubtotalRow[] = [];
  const rowIndexMap: Record<string, number> = {};

  // Helper to get the group key from a row
  const getGroupKey = (row: SubtotalRow): string => {
    return groupColumnIds.map((id) => String(row[id] ?? '')).join('|');
  };

  // Process each row
  rows.forEach((row) => {
    const groupKey = getGroupKey(row);
    let pivotedRow: SubtotalRow;

    // Get or create the row for this group
    if (rowIndexMap[groupKey] === undefined) {
      pivotedRow = {};

      // Copy group column values
      groupColumnIds.forEach((id) => {
        pivotedRow[id] = row[id];
      });

      rowIndexMap[groupKey] = pivotedRows.length;
      pivotedRows.push(pivotedRow);
    } else {
      pivotedRow = pivotedRows[rowIndexMap[groupKey]];
    }

    // Create pivoted columns for each value column
    valueColumnIds.forEach((valueId) => {
      // Create a unique pivot column name
      const pivotColumnName = [...pivotColumnIds.map((pivotId) => row[pivotId]), valueId].join(
        PIVOT_SEPARATOR,
      );

      // Store the value in the pivoted column
      pivotedRow[pivotColumnName] = row[valueId];
    });
  });

  return pivotedRows;
}

/**
 * Fetches both grand totals and subtotals for PDF and XLSX Formatted export.
 *
 * Orchestrates parallel fetching of:
 * - Grand totals: overall totals across the whole dataset
 * - Subtotals: totals for each grouping level with lookup maps for O(1) access
 *
 * @param vif - The VIF configuration
 * @param displayableColumns - Columns to display in the export
 * @param legacyWhereClause - WHERE clause for filtering data (QFB/GFB)
 * @param hierarchyConfig - Optional hierarchy configuration defining grouping levels
 * @param nonStandardAggregations - Optional custom aggregation definitions
 * @param applyAdvancedFormulas - When true (default), applies Advanced Totaling custom formulas
 *   from hierarchy column configurations. When false, only standard aggregations are used -- this
 *   is what the non-advanced export path passes so totals are computed on the whole dataset
 *   without pulling in Advanced Totaling formula semantics.
 * @param isAllDataExport - When true, the user picked "all data" in the xlsx-formatted export
 *   modal. The pivot subtotal builder drops its 100k inner-pipe cap so totals reflect every row.
 *   Grand totals already run uncapped via grandTotalQuery's flat composeQuery shape.
 * @returns Object containing grand totals, subtotal lookup maps, and metadata
 */
export async function fetchAllTotalsForExport(
  vif: Vif,
  displayableColumns: ViewColumn[],
  exportType: TableExportType,
  legacyWhereClause?: string,
  hierarchyConfig?: Hierarchy,
  nonStandardAggregations?: ColumnAggregation[] | null,
  applyAdvancedFormulas: boolean = true,
  isAllDataExport: boolean = false,
): Promise<ExportTotalsResult> {
  // an empty columnConfigurations array would still reach constructAgRequestFromHierarchy and
  // produce an agRequest with no valueCols, which composes to malformed SoQL. skip it.
  if (!hierarchyConfig?.columnConfigurations?.length) {
    return {
      grandTotals: null,
      subtotalLookup: new Map<string, unknown>(),
      levelMetadata: new Map<number, { groupColumns: string[]; valueColumns: string[] }>(),
    };
  }

  const agRequest = constructAgRequestFromHierarchy(hierarchyConfig);
  // when applyAdvancedFormulas is false the non-advanced export path still wants server-side
  // totals, but with standard aggregations only -- so withhold columnConfigurations from the
  // grand total query. subtotals get the same gating threaded through fetchSubTotalsForExport.
  const grandTotalHierarchyColumnConfigs = applyAdvancedFormulas
    ? hierarchyConfig.columnConfigurations
    : undefined;
  const isPivot = isPivotInHierarchy(hierarchyConfig);

  const [grandTotals, { subtotalLookup, levelMetadata }] = await Promise.all([
    fetchGrandTotalsForExport(
      vif,
      displayableColumns,
      agRequest,
      grandTotalHierarchyColumnConfigs,
      legacyWhereClause,
      nonStandardAggregations,
      isPivot,
    ),
    fetchSubTotalsForExport(
      vif,
      displayableColumns,
      agRequest,
      exportType,
      hierarchyConfig,
      legacyWhereClause,
      nonStandardAggregations,
      applyAdvancedFormulas,
      isAllDataExport,
    ),
  ]);

  return {
    grandTotals,
    subtotalLookup,
    levelMetadata,
  };
}
